// teamMembersPanelSlice.js — Redux slice for the Dashboard's Team Members panel
// (TeamMembersPanel.jsx). Same lightweight axios pattern as lifecycleStageSlice.js:
// no manual auth header, workspace scoping resolved server-side off req.workspaceId.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

// Server-paginated + filtered roster (see getTeamMembersPanel.service.js).
export const fetchTeamMembersPanel = createAsyncThunk(
  "teamMembersPanel/fetch",
  async ({ teamName, status, page = 1, limit = 5 } = {}, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/teammembers/dashboard-panel`, {
        params: { teamName: teamName || undefined, status: status || undefined, page, limit },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Cheap {id, teamName, contactAssignedCount} list to populate the Team filter
// dropdown — reuses the existing GET /api/teams/contact-count endpoint
// (teamsController.getTeamNameAndContactCount) rather than the heavier
// /api/teams/get, which hydrates every team's full member/contact objects.
export const fetchDashboardTeamOptions = createAsyncThunk(
  "teamMembersPanel/fetchTeamOptions",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/teams/contact-count`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const initialState = {
  members: [],
  totalCount: 0,
  page: 1,
  limit: 5,
  loading: false,
  error: null,
  teamOptions: [], // [{ id, teamName, contactAssignedCount }]
  teamOptionsLoading: false,
};

const teamMembersPanelSlice = createSlice({
  name: "teamMembersPanel",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTeamMembersPanel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamMembersPanel.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload.members;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchTeamMembersPanel.rejected, (state, action) => {
        state.loading = false;
        state.members = [];
        state.error = action.payload;
      })
      .addCase(fetchDashboardTeamOptions.pending, (state) => {
        state.teamOptionsLoading = true;
      })
      .addCase(fetchDashboardTeamOptions.fulfilled, (state, action) => {
        state.teamOptionsLoading = false;
        state.teamOptions = action.payload;
      })
      .addCase(fetchDashboardTeamOptions.rejected, (state) => {
        state.teamOptionsLoading = false;
        state.teamOptions = [];
      });
  },
});

export default teamMembersPanelSlice.reducer;
